import React from 'react';
import PropTypes from 'prop-types';
import { Modal, Image, Button, Form } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Select from 'react-select';
import confirmIcon from '../../../assets/confirm.svg';
import styles from './SuspendUserModal.module.scss';
import { suspendUserSchema } from '../../../validations/yupValidations';
import TextAreaField from '../../TextArea/TextArea';

const SUSPENSION_REASONS = [
  { label: 'Inappropriate behavior', value: 'inappropriate_behavior' },
  { label: 'Violating community guidelines', value: 'terms_violation' },
  { label: 'Spam and misuse', value: 'spam_activity' },
  { label: 'False information', value: 'false_information' },
  { label: 'Non-compliance with platform terms and conditions', value: 'non_compliance' },
  { label: 'Privacy violations', value: 'privacy_violations' },
  { label: 'Other reasons', value: 'other' },
];

function SuspendUserModal({ show, context, handleClose, onConfirm, onCancel }) {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(suspendUserSchema),
  });

  const handleFormSubmit = (data) => {
    onConfirm(data.reason?.label || data.reason, data.details);
  };

  const handleFormError = () => {
    // Form validation failed, errors will be displayed automatically
  };

  const handleCancel = (e) => {
    reset({
      reason: null,
      details: '',
    });
    onCancel(e);
  };

  return (
    <Modal
      show={show}
      onHide={() => {
        handleClose();
        reset();
      }}
      className="delPopContainer"
      centered
    >
      <Modal.Body>
        <Image className={styles.confirmIcon} src={confirmIcon} alt="Suspend user confirmation" />
        <p>{`Suspend this${context ? ' ' : ''}${context.toLowerCase()}?`}</p>

        <Form onSubmit={handleSubmit(handleFormSubmit, handleFormError)}>
          <div className={styles.fieldContainer}>
            <label className={styles.fieldLabel} htmlFor="reason">
              Reason for Suspension
              <span style={{ color: 'red', marginLeft: '3px' }}>*</span>
            </label>
            <Controller
              name="reason"
              control={control}
              render={({ field: { value, onChange, onBlur } }) => (
                <Select
                  options={SUSPENSION_REASONS}
                  placeholder="Select a reason for suspension"
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                  className={styles.selectInput}
                />
              )}
            />
            {errors.reason && <p className={styles.errorMessage}>{errors.reason.message}</p>}
          </div>

          <div className={styles.fieldContainer}>
            <TextAreaField
              fieldName="details"
              label="Details"
              labelstyle={styles.fieldLabel}
              register={register}
              errors={errors}
              placeHolder="Provide additional details about the suspension..."
              isRequired
              mandatory
              className={styles.textAreaInput}
              maxLength={200}
            />
          </div>

          <div className="btn_container">
            <Button className={styles.resetbtn} onClick={handleCancel}>
              <svg width="22" height="22" viewBox="0 0 22 22" xmlns="http://www.w3.org/2000/svg">
                <path d="M7 10C6.73478 10 6.48043 10.1054 6.29289 10.2929C6.10536 10.4804 6 10.7348 6 11C6 11.2652 6.10536 11.5196 6.29289 11.7071C6.48043 11.8946 6.73478 12 7 12H15C15.2652 12 15.5196 11.8946 15.7071 11.7071C15.8946 11.5196 16 11.2652 16 11C16 10.7348 15.8946 10.4804 15.7071 10.2929C15.5196 10.1054 15.2652 10 15 10H7Z" />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M22 11C22 17.075 17.075 22 11 22C4.925 22 0 17.075 0 11C0 4.925 4.925 0 11 0C17.075 0 22 4.925 22 11ZM20 11C20 12.1819 19.7672 13.3522 19.3149 14.4442C18.8626 15.5361 18.1997 16.5282 17.364 17.364C16.5282 18.1997 15.5361 18.8626 14.4442 19.3149C13.3522 19.7672 12.1819 20 11 20C9.8181 20 8.64778 19.7672 7.55585 19.3149C6.46392 18.8626 5.47177 18.1997 4.63604 17.364C3.80031 16.5282 3.13738 15.5361 2.68508 14.4442C2.23279 13.3522 2 12.1819 2 11C2 8.61305 2.94821 6.32387 4.63604 4.63604C6.32387 2.94821 8.61305 2 11 2C13.3869 2 15.6761 2.94821 17.364 4.63604C19.0518 6.32387 20 8.61305 20 11Z"
                />
              </svg>
              Cancel
            </Button>
            <Button type="submit" className={styles.resetPasswordBtn}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM10 15C9.45 15 9 14.55 9 14V6C9 5.45 9.45 5 10 5C10.55 5 11 5.45 11 6V14C11 14.55 10.55 15 10 15Z"
                  fill="white"
                />
              </svg>
              Suspend
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

SuspendUserModal.propTypes = {
  show: PropTypes.bool.isRequired,
  context: PropTypes.string,
  handleClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

SuspendUserModal.defaultProps = {
  context: '',
};

export default SuspendUserModal;
